/* global dataLayer */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import MediaQuery from 'react-responsive';
import NoSSR from 'react-no-ssr';
import { withClientConfig, ExperimentsConsumer } from '@spotahome/bff/client';
import Metrics from '@spotahome/soyuz-metrics';
import MobileDetect from 'mobile-detect';
import '@spotahome/ui-library/lib/components/InstantBookingBanner.css';

import mapPointShape from '@search/shapes/mapPoint';
import poiShape from '@search/shapes/poi';
import {
  adaptFilters,
  getDefaultFilters,
  adaptFiltersToUrl
} from '@search/utils/filters';
import { shiftMarkers } from '../../../../pages/search/utils/markerShifter';

import Paginator from '@search/components/Paginator';

import getMarkersQuery from '@common/graphql/queries/markers';
import getGqlClient from '@common/graphql/client';

import { getMarkersRequest } from '@common/api/marketplace-client';
import { getUnitPricesRequest } from '@common/api/marketplace-client';
import { setPoiRequest, deletePoiRequest } from '@common/api/poi-client';

import debounce from '@common/utils/debounce';

import getInternalLinkingContainer from '@common/components/internalLinking';
import HomeCardList from '@common/components/HomeCardList';
import TotalResults from '@search/components/TotalResults';

import IconLoading from '../../ui/icon/icon-loading';
import AdUrlService from '../../cornucopia/ads/ad-url-service';
import MapsAPI from '../../ui/map/maps-api';

import CardPaginator from '../lib/card-paginator';

import ContextBar from './context-bar'; // TODO: requires window

import Filters from './Filters';
import FiltersHeader from './filters-header';

import Map from './Map';

// If a POI is defined, the list of markers will be retrieved ordered by distance
// and the frontend will order by relevance each set defined by this const.
const MAP_ID = 'themap';
const MQ_TABLET_MAX_WIDTH = '700px';
const PLACE_SELECTED_EVENT_CATEGORY = 'interactive-map-add-reference-point';
const INITIAL_STATE = {
  maxTotalResults: 0,
  adIdCardMouseEnter: null,
  adIdCardMouseLeave: null,
  adMarkerClicked: null,
  adMarkerMouseEnter: null,
  adMarkerMouseLeave: null,
  isFiltersHidden: true,
  unitPrices: [],
  isMenuOpen: false,
  canUpdateBoundaries: false
};

const InternalLinking = getInternalLinkingContainer('SearchPage');

const serializeGaParameters = (areaId, serializeArguments) =>
  [
    areaId,
    serializeArguments['move-in'],
    serializeArguments['move-out'],
    serializeArguments.budget,
    serializeArguments['type[]'].join(),
    serializeArguments['topology[]'].join(),
    serializeArguments['bathrooms[]'].join(),
    serializeArguments.bed,
    serializeArguments['features[]'].join(),
    serializeArguments.poi,
    serializeArguments.totalHomecards,
    serializeArguments.totalHomecardsWithIB,
    serializeArguments.verified,
    '',
    '',
    serializeArguments.instantBooking,
    serializeArguments['areaId[]'].join(),
    serializeArguments.flexibleMoveIn,
    serializeArguments.flexibleMoveOut
  ].join(';');

const sendCityDateEvent = (areaId, parameters) => {
  const gaParameters = serializeGaParameters(areaId, parameters);
  Metrics.ga.sendEventWithClientId('dwh-city-date', gaParameters);
};

const checkMapHidden = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(max-width: 700px)').matches;

const parsePointOfInterestParam = pointOfInterest => {
  const pieces = pointOfInterest.split(',');

  if (pieces.length !== 2) {
    return null;
  }

  return {
    lat: parseFloat(pieces[0], 10),
    lng: parseFloat(pieces[1], 10),
    name: '',
    placeId: ''
  };
};

const sendPageView = (areaId, filters) => {
  const url = AdUrlService.getSearchUrlFromFilters(areaId, filters);

  Metrics.ga.set('page', url);
  Metrics.ga.sendPageView();
};

const isPointOfInterestInBounds = (pointOfInterest, limits) => {
  if (!pointOfInterest) {
    return false;
  }

  const boundsNeSw = {
    ne: {
      lat: limits.ne.lat,
      lng: limits.ne.lng
    },
    sw: {
      lat: limits.sw.lat,
      lng: limits.sw.lng
    }
  };

  return MapsAPI.coordsInGivenBounds(pointOfInterest, boundsNeSw);
};

const pushDataLayerProductList = (nextMarkers, moveIn, moveOut) => {
  if (typeof dataLayer !== 'undefined') {
    const productIdList = nextMarkers.slice(0, 3).map(ru => ru.id);

    dataLayer.push({
      PageType: 'Listingpage',
      HashedEmail: '',
      ProductIDList: productIdList,
      din: moveIn,
      dout: moveOut
    });
  }
};

class Search extends PureComponent {
  constructor(props) {
    super(props);

    const filters = {
      ...getDefaultFilters(),
      ...props.initialFiltersFromQuery
    };

    let pointOfInterest = null;

    if (filters.poi) {
      pointOfInterest = parsePointOfInterestParam(filters.poi);
      if (
        props.sessionPoi &&
        pointOfInterest.lat === props.sessionPoi.lat &&
        pointOfInterest.lng === props.sessionPoi.lng
      ) {
        pointOfInterest = props.sessionPoi;
      }
    } else if (props.sessionPoi) {
      pointOfInterest = props.sessionPoi;
    }

    this.state = {
      ...INITIAL_STATE,
      markerList: props.initialMarkerList,
      numberOfMarkers: props.numberOfMarkers,
      isMapHidden: checkMapHidden(),
      filters,
      pointOfInterest,
      unitPrices: props.initialUnitPrices
    };

    this.cardPaginator = new CardPaginator({ pageSize: props.itemsPerPage });

    this.cardPaginator.initializeData(this.state.markerList);

    this.loadingIcon = React.createRef();
  }

  componentDidMount() {
    const { filters, pointOfInterest, markerList } = this.state;

    if (pointOfInterest) {
      this.updatePointOfInterestState(pointOfInterest);
    }

    if (markerList.length > 0) {
      pushDataLayerProductList(
        markerList,
        filters['move-in'],
        filters['move-out']
      );
    }

    window.addEventListener(
      'resize',
      debounce(() => {
        const isMapHiddenNow = checkMapHidden();
        if (isMapHiddenNow !== this.state.isMapHidden) {
          this.setState({ isMapHidden: isMapHiddenNow });
        }
      }, 250)
    );

    this.requestAllMarkers();
  }

  componentDidUpdate(prevProps, prevState) {
    const prevFilters = prevState.filters;

    if (JSON.stringify(this.state.filters) !== JSON.stringify(prevFilters)) {
      const initialHomecards = Object.values(prevProps.initialHomecards);
      const totalHomecards = initialHomecards.length;

      const totalHomecardsWithIB = initialHomecards.filter(
        homecard => homecard.isInstantBooking
      ).length;

      const serializeArguments = {
        ...this.state.filters,
        totalHomecards,
        totalHomecardsWithIB
      };

      sendCityDateEvent(this.props.area.id, serializeArguments);

      this.requestNewContent(prevFilters);
    }
  }

  onPlaceSelected = async place => {
    const { name, lat, lng } = place;

    if (name && lat && lng) {
      Metrics.ga.sendEvent(
        PLACE_SELECTED_EVENT_CATEGORY,
        name,
        `${lat},${lng}`
      );
    }

    // TODO: check if it works
    try {
      const city = this.props.area.id;
      await setPoiRequest(city, place);
    } catch (error) {
      console.error(error);
    }

    this.updatePointOfInterestState(place);
  };

  onPlaceUnselected = async () => {
    try {
      const city = this.props.area.id;
      await deletePoiRequest(city);
    } catch (error) {
      console.error(error);
    }

    this.updatePointOfInterestState(null);
  };

  onCardMouseEnter = adId => {
    this.setState({
      adMarkerClicked: null,
      adIdCardMouseEnter: adId,
      adIdCardMouseLeave: null
    });
  };

  onCardMouseLeave = adId => {
    this.setState({
      adMarkerClicked: null,
      adIdCardMouseEnter: null,
      adIdCardMouseLeave: adId
    });
  };

  onMarkerClicked = adIdCount => {
    if (
      this.state.adMarkerClicked &&
      adIdCount &&
      adIdCount.id === this.state.adMarkerClicked.id
    ) {
      this.setState({
        adMarkerClicked: null,
        isFiltersHidden: true
      });
    } else {
      this.setState({
        adMarkerClicked: adIdCount,
        isFiltersHidden: true
      });
    }
  };

  onMarkerMouseEnter = adIdCount => {
    this.setState({
      adMarkerMouseEnter: adIdCount,
      adMarkerMouseLeave: null
    });
  };

  onMarkerMouseLeave = adIdCount => {
    this.setState({
      adMarkerMouseLeave: adIdCount,
      adMarkerMouseEnter: null
    });
  };

  onNextButtonClick = () => {
    if (this.cardPaginator.getNextPageNumber()) {
      this.cardPaginator.nextPage();
      this.onNewPageContent();
    }
  };

  onPreviousButtonClick = () => {
    if (this.cardPaginator.getPreviousPageNumber()) {
      this.cardPaginator.previousPage();
      this.onNewPageContent();
    }
  };

  onMapWillUnmount = () => {
    this.cardPaginator.restoreOriginalData();
    this.onNewPageContent();
  };

  onMapDidMount = map => {
    this.boundariesFilter = elem =>
      map.isMarkerInBoundaries({
        lat: elem.coord[1],
        lng: elem.coord[0]
      });
  };

  onNewPageContent = () => {
    const currentPageMarkers = this.cardPaginator.getCurrentPageContent();

    this.setState(
      prevState => ({
        markerList: currentPageMarkers,
        adMarkerClicked: null,
        adIdCardMouseEnter: null,
        adIdCardMouseLeave: null,
        filters: {
          ...prevState.filters,
          page: this.cardPaginator.getCurrentPageNumber()
        }
      }),
      () => {
        this.updateURL(this.state.filters);
      }
    );
  };

  getMarkers = async queryStr => {
    this.setIconLoadingHidden(false);
    let newMarkers = [];

    try {
      const { data } = await getMarkersRequest(this.props.area.id, {
        queryStr
      });

      newMarkers = data.ok ? data.data : [];
    } catch (error) {
      if (error.message !== 'CANCELED') {
        console.error(error);
      }
    }

    this.updateMarkersState(newMarkers);
    this.setIconLoadingHidden(true);
  };

  getUnitPrices = async queryStr => {
    try {
      const { data } = await getUnitPricesRequest(this.props.area.id, {
        queryStr
      });

      if (data.ok) {
        this.setState({
          unitPrices: data.unitPrices.filter(
            price => price <= this.props.maxPriceInCity
          )
        });
      }
    } catch (error) {
      if (error.message !== 'CANCELED') {
        console.error(error);
      }
    }
  };

  getCurrentPage = () => this.cardPaginator.getCurrentPageNumber();

  setIconLoadingHidden = hidden => {
    this.loadingIcon.current.hidden = hidden;
  };

  handleFiltersToggleClick = () => {
    this.setState({
      isFiltersHidden: !this.state.isFiltersHidden
    });
  };

  updatePointOfInterestState = pointOfInterest => {
    let coordsString = '';
    let point = pointOfInterest;

    if (isPointOfInterestInBounds(point, this.props.area.limits)) {
      coordsString = `${point.lat},${point.lng}`;
    } else {
      point = null;
    }

    this.updateFiltersState({ poi: coordsString });
    this.setState({ pointOfInterest: point });
  };

  updateMarkersState = nextMarkers => {
    const numberOfMarkers = nextMarkers.length;
    const maxTotalResults =
      this.state.maxTotalResults > numberOfMarkers
        ? this.state.maxTotalResults
        : numberOfMarkers;

    if (this.props.map !== 'original') nextMarkers = shiftMarkers(nextMarkers);

    this.cardPaginator.initializeData(nextMarkers);
    this.cardPaginator.filter(this.boundariesFilter);

    this.setState(
      {
        nextMarkers,
        maxTotalResults,
        numberOfMarkers
      },
      this.onNewPageContent
    );
  };

  updateSortBy = sortBy => {
    this.updateFiltersState({ sortBy });
  };

  toggleMenu = () =>
    this.setState(({ isMenuOpen }) => ({ isMenuOpen: !isMenuOpen }));

  updateURL = filters => {
    AdUrlService.setSearchLocationUrl(this.props.area.id, filters);
  };

  isInstantBooking = () => {
    const initialHomecards = Object.values(this.props.initialHomecards);
    return initialHomecards.some(homecard => homecard.isInstantBooking);
  };

  calculateMarkersWithNewBounds = () => {
    if (this.state.canUpdateBoundaries) {
      this.cardPaginator.filter(this.boundariesFilter);
      this.onNewPageContent();
    }
  };

  calculateMarkersWithoutBounds = () => {
    this[MAP_ID]._setInitializePosition(true);
    this.cardPaginator.restoreOriginalData();
    this.onNewPageContent();
  };

  isPrevButtonDisabled = () =>
    this.cardPaginator.getPreviousPageNumber() === null;

  isNextButtonDisabled = () => this.cardPaginator.getNextPageNumber() === null;

  requestAllMarkers = async () => {
    try {
      const { filters } = this.state;
      const { data } = await getMarkersQuery(getGqlClient())(
        this.props.area.id,
        adaptFilters(filters, this.props.sortingAlgorithm)
      );

      const markers =
        this.props.map === 'original'
          ? data.search.markers
          : shiftMarkers(data.search.markers);

      this.cardPaginator.initializeData(markers);
      this.cardPaginator.setCurrentPage(parseInt(filters.page, 10));
      this.setState({
        numberOfMarkers: markers.length,
        canUpdateBoundaries: true,
        hasInstantBooking: markers.some(marker => marker.instantBooking)
      });
    } catch (error) {
      console.error(error);
    }
  };

  requestNewContent = debounce(prevFilters => {
    const currentFilters = adaptFiltersToUrl(
      this.state.filters,
      this.props.sortingAlgorithm
    );
    const prevFiltersAdapted = adaptFiltersToUrl(
      prevFilters,
      this.props.sortingAlgorithm
    );
    const urlQueryString = AdUrlService.createQueryString(currentFilters);

    sendPageView(this.props.area.id, prevFiltersAdapted);

    if (this.shouldUpdateMarkers(prevFiltersAdapted)) {
      this.getMarkers(urlQueryString);
    }

    if (this.shouldUpdatePrices(prevFiltersAdapted)) {
      this.getUnitPrices(urlQueryString);
    }
  }, 250);

  shouldUpdateMarkers = prevFilters => {
    const currentFilters = this.state.filters;
    const filterKeys = Object.keys(currentFilters).filter(
      key => key !== 'page'
    );

    return filterKeys.some(key => currentFilters[key] !== prevFilters[key]);
  };

  shouldUpdatePrices = prevFilters => {
    const currentFilters = this.state.filters;
    const filterKeys = Object.keys(currentFilters).filter(
      key => key !== 'page' && key !== 'sortBy'
    );

    return filterKeys.some(key => currentFilters[key] !== prevFilters[key]);
  };

  updateFiltersState = filtersToBeMerged => {
    const filters2merge = Object.assign(
      {},
      this.state.filters,
      filtersToBeMerged
    );

    const newState = Object.assign({}, this.state, {
      filters: filters2merge
    });

    // remove rooms when user deselect apartments
    if (
      filtersToBeMerged['type[]'] &&
      !filtersToBeMerged['type[]'].includes('apartments')
    ) {
      newState.filters['topology[]'] = [];
    }

    this.setState(newState);
  };

  isMobile = () => {
    if (typeof window !== 'undefined') {
      const md = new MobileDetect(window.navigator.userAgent);
      return md.phone();
    }
  };

  clearFilters = () => {
    if (!this.isMobile) this[MAP_ID]._setInitializePosition(true);
    this.updateFiltersState(getDefaultFilters());
  };

  renderFullFilters = () =>
    !this.state.isFiltersHidden && (
      <div>
        <div className="search-filters-full">
          <Filters
            currency={this.props.currency}
            filters={this.state.filters}
            hasInstantBooking={this.state.hasInstantBooking}
            maxPriceInCity={this.props.maxPriceInCity}
            simpleFilters={false}
            unitPrices={this.state.unitPrices}
            updateFiltersWith={this.updateFiltersState}
            cityName={this.props.area.id}
          />
        </div>
        <FiltersHeader
          results={this.state.numberOfMarkers}
          areaSlug={this.props.area.id}
          totalResults={this.cardPaginator.getOriginalLength()}
          handleFiltersToggle={this.handleFiltersToggleClick}
          maxTotalResults={this.state.maxTotalResults}
          filters={this.state.filters}
          expandedFilters
        />
      </div>
    );

  renderMap = () => {
    const defaultZoom = this.props.clientConfig.defaultZoom[this.props.area.id];

    return (
      !this.state.isMapHidden && (
        <Map
          ref={map => {
            this[MAP_ID] = map;
          }}
          mapId={MAP_ID}
          currencyIsoCode={this.props.currency.isoCode}
          area={this.props.area}
          defaultZoom={defaultZoom}
          markerList={this.state.markerList}
          pointOfInterest={this.state.pointOfInterest}
          adIdCardMouseEnter={this.state.adIdCardMouseEnter}
          adIdCardMouseLeave={this.state.adIdCardMouseLeave}
          adMarkerClicked={this.state.adMarkerClicked}
          adMarkerMouseEnter={this.state.adMarkerMouseEnter}
          adMarkerMouseLeave={this.state.adMarkerMouseLeave}
          onMarkerClicked={this.onMarkerClicked}
          onMarkerMouseEnter={this.onMarkerMouseEnter}
          onMarkerMouseLeave={this.onMarkerMouseLeave}
          onMapDidMount={this.onMapDidMount}
          onMapWillUnmount={this.onMapWillUnmount}
          onIdle={this.calculateMarkersWithNewBounds}
        />
      )
    );
  };

  renderPaginator = () => {
    const areaId = this.props.area.id;
    const { filters } = this.state;
    const nextIsDisabled = this.isNextButtonDisabled();
    const previousIsDisabled = this.isPrevButtonDisabled();

    const nextPageUrl = nextIsDisabled
      ? ''
      : AdUrlService.getSearchUrlFromFilters(areaId, {
          ...filters,
          page: this.cardPaginator.getNextPageNumber()
        });

    const previousPageUrl = previousIsDisabled
      ? ''
      : AdUrlService.getSearchUrlFromFilters(areaId, {
          ...filters,
          page: this.cardPaginator.getPreviousPageNumber()
        });

    return (
      this.cardPaginator.getTotalPages() > 1 && (
        <Paginator
          previousButtonDisabled={previousIsDisabled}
          nextButtonDisabled={nextIsDisabled}
          onPreviousButtonClick={this.onPreviousButtonClick}
          onNextButtonClick={this.onNextButtonClick}
          nextPageUrl={nextPageUrl}
          previousPageUrl={previousPageUrl}
        />
      )
    );
  };

  render() {
    const totalResults = this.cardPaginator.getOriginalLength();

    return (
      <div className="layout-search">
        <div className="search-sidebar">
          <div id="search-scroll" className="search-scroll">
            <NoSSR>
              <div className="search-filters">
                <Filters
                  simpleFilters
                  filters={this.state.filters}
                  updateFiltersWith={this.updateFiltersState}
                  unitPrices={this.state.unitPrices}
                  currency={this.props.currency}
                  maxPriceInCity={this.props.maxPriceInCity}
                  areaId={this.props.area.areaId}
                  minimumStaying={this.props.area.minimumStaying}
                  cityName={this.props.area.id}
                />
              </div>
              <div className="search-results">
                <FiltersHeader
                  results={this.state.numberOfMarkers}
                  totalResults={totalResults}
                  handleFiltersToggle={this.handleFiltersToggleClick}
                  maxTotalResults={this.state.maxTotalResults}
                  expandedFilters={false}
                  orderBy={this.state.filters.sortBy}
                  handleSortChange={this.updateSortBy}
                  areaSlug={this.props.area.id}
                  filters={this.state.filters}
                />
              </div>
            </NoSSR>
            <div className="search-listing">
              <div className="search-listing__total-results">
                <TotalResults
                  types={this.state.filters['type[]']}
                  areaSlug={this.props.area.id}
                  totalResults={this.state.numberOfMarkers}
                />
              </div>
              <HomeCardList
                currentPage={this.cardPaginator.getCurrentPageNumber()}
                hasMap={!this.state.isMapHidden}
                initialHomecards={this.props.initialHomecards}
                results={this.cardPaginator.getLength()}
                totalResults={this.cardPaginator.getOriginalLength()}
                markerList={this.state.markerList}
                currency={this.props.currency}
                filters={this.state.filters}
                onCardMouseEnter={this.onCardMouseEnter}
                onCardMouseLeave={this.onCardMouseLeave}
                adMarkerClicked={this.state.adMarkerClicked}
                clearExtendedFilters={this.clearFilters}
                calculateMarkersWithoutBounds={
                  this.calculateMarkersWithoutBounds
                }
                pointOfInterest={this.state.pointOfInterest}
              />
              {this.renderPaginator()}
              <InternalLinking />
              <div
                className="spinner-loading-map"
                id="spinner-list-loader"
                hidden
              >
                <IconLoading />
              </div>
            </div>
          </div>
          <NoSSR>
            <MediaQuery maxWidth={MQ_TABLET_MAX_WIDTH}>
              <ContextBar
                area={this.props.area}
                pointOfInterest={this.state.pointOfInterest}
                onPlaceSelected={this.onPlaceSelected}
                onPlaceUnselected={this.onPlaceUnselected}
                countryCode={this.props.countryCode}
                toggleMenu={this.toggleMenu}
              />
            </MediaQuery>
            <ReactCSSTransitionGroup
              transitionName="search-filters"
              transitionEnterTimeout={500}
              transitionLeaveTimeout={200}
            >
              {this.renderFullFilters()}
            </ReactCSSTransitionGroup>
          </NoSSR>
        </div>
        <section className="search-map">
          <NoSSR>
            <MediaQuery minWidth={MQ_TABLET_MAX_WIDTH}>
              <ContextBar
                className="search-context--map"
                area={this.props.area}
                pointOfInterest={this.state.pointOfInterest}
                onPlaceSelected={this.onPlaceSelected}
                onPlaceUnselected={this.onPlaceUnselected}
                isFiltersHidden={this.state.isFiltersHidden}
                countryCode={this.props.countryCode}
              />
            </MediaQuery>
            {this.renderMap()}
            <div className="spinner-loading" ref={this.loadingIcon} hidden>
              <IconLoading />
            </div>
          </NoSSR>
        </section>
      </div>
    );
  }
}

Search.propTypes = {
  sessionPoi: PropTypes.shape(poiShape),
  area: PropTypes.shape({
    id: PropTypes.string.isRequired,
    areaId: PropTypes.number.isRequired,
    limits: PropTypes.shape({
      ne: PropTypes.shape(mapPointShape).isRequired,
      sw: PropTypes.shape(mapPointShape).isRequired
    }).isRequired,
    minimumStaying: PropTypes.number.isRequired
  }).isRequired,
  numberOfResults: PropTypes.number,
  countryCode: PropTypes.string,
  currency: PropTypes.object,
  maxPriceInCity: PropTypes.number.isRequired,
  initialFiltersFromQuery: PropTypes.object,
  clientConfig: PropTypes.shape({
    defaultZoom: PropTypes.shape({
      [PropTypes.string]: PropTypes.number
    }).isRequired
  }).isRequired,
  numberOfMarkers: PropTypes.number.isRequired,
  sortingAlgorithm: PropTypes.string,
  itemsPerPage: PropTypes.number.isRequired,
  map: PropTypes.string.isRequired
};

Search.defaultProps = {
  sessionPoi: {},
  initialFiltersFromQuery: {},
  sortingAlgorithm: ''
};

export default withClientConfig()(Search);
